import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Milestone as MilestoneIcon } from 'lucide-react';
import MilestoneDetailDialog from './MilestoneDetailDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map a YYYY-MM-DD string to "Mon YYYY" for the milestone-tile date suffix.
function fmtShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// Visual departmental roadmap. Each colour band = one production
// department (Engineering, Rooms, F&B, Brand Standards, Sales &
// Marketing, Spa). Hard-coded PHASES below are the *fallback* shown
// when a band has no phases tagged with that department in the
// database. As soon as an admin tags one or more phases with
// `department == "<Band Title>"` from Admin → Planning, the live
// phases REPLACE the fallback tiles automatically.

// iter-110 — User-specified production department list. Replaces the
// previous 6-band hardcoded set (Engineering, Rooms, F&B, Brand
// Standards, Sales & Marketing, Spa). Each band still falls back to a
// generic milestone scaffold when its department has no live phases in
// the database. Hyatt-aligned colours kept consistent with brand audit.
const DEFAULT_TASKS = (deptLabel) => ([
  { name: 'Planning · Mar 2026', description: `${deptLabel} planning, scope, and SOPs locked. Resource and budget envelope approved by ownership.` },
  { name: 'Procurement · Jun 2026', description: `Equipment, FF&E, software, and contracted services for ${deptLabel} sourced against the master Gantt.` },
  { name: 'Build · Oct 2026', description: `${deptLabel} spaces, systems, or programs handed over and tested under live conditions.` },
  { name: 'Train · Jan 2027', description: `${deptLabel} staff onboarded to Hyatt standards, procedures, and tooling. Cross-department playbooks rehearsed.` },
  { name: 'Soft Open · Feb 2027', description: `${deptLabel} runs at controlled capacity during friends-and-family. KPIs validated under load.` },
  { name: 'Optimize · Mar 2027', description: `${deptLabel} KPIs tuned against benchmarks. 30/60/90-day remediation plan delivered to ownership.` },
]);

const PHASES = [
  { num: 1,  title: 'F&B',           gradient: 'linear-gradient(90deg, #14B8A6 0%, #0D9488 100%)', accent: '#14B8A6' },
  { num: 2,  title: 'Rooms',         gradient: 'linear-gradient(90deg, #A3A834 0%, #84CC16 100%)', accent: '#84CC16' },
  { num: 3,  title: 'Engineering',   gradient: 'linear-gradient(90deg, #F59E0B 0%, #FB923C 50%, #FACC15 100%)', accent: '#F59E0B' },
  { num: 4,  title: 'Spa',           gradient: 'linear-gradient(90deg, #BE185D 0%, #EC4899 100%)', accent: '#EC4899' },
  { num: 5,  title: 'HR',            gradient: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)', accent: '#6366F1' },
  { num: 6,  title: 'Sales',         gradient: 'linear-gradient(90deg, #7C3AED 0%, #8B5CF6 100%)', accent: '#8B5CF6' },
  { num: 7,  title: 'Water Sports',  gradient: 'linear-gradient(90deg, #0EA5E9 0%, #06B6D4 100%)', accent: '#06B6D4' },
  { num: 8,  title: 'Security',      gradient: 'linear-gradient(90deg, #1E40AF 0%, #2563EB 100%)', accent: '#1E40AF' },
  { num: 9,  title: 'Entertainment', gradient: 'linear-gradient(90deg, #DC2626 0%, #F97316 100%)', accent: '#DC2626' },
  { num: 10, title: 'IT Projects',   gradient: 'linear-gradient(90deg, #475569 0%, #0F172A 100%)', accent: '#475569' },
  { num: 11, title: 'Finance',       gradient: 'linear-gradient(90deg, #15803D 0%, #16A34A 100%)', accent: '#15803D' },
  { num: 12, title: 'Laundry',       gradient: 'linear-gradient(90deg, #64748B 0%, #94A3B8 100%)', accent: '#64748B' },
].map((p) => ({
  ...p,
  leftMarker: 'Mar 2026',
  rightMarker: 'Mar 2027',
  timeline: 'March 2026 – March 2027',
  summary: `${p.title} reconstruction roadmap — milestones tagged with "${p.title}" from Admin → Planning automatically replace these placeholders.`,
  tasks: DEFAULT_TASKS(p.title),
}));

const KPIS = ['GSS: Intent to Recommend', 'WOH Penetration', 'Colleague Engagement'];

function Marker({ text, side }) {
  if (!text) return null;
  return (
    <div
      className={`hidden lg:flex flex-shrink-0 items-center justify-center w-24 text-center text-[10px] font-bold leading-tight px-2 ${side === 'right' ? 'order-last' : ''}`}
      style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
    >
      <span
        className="block px-2 py-1 rounded-lg"
        style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}
      >
        {text}
      </span>
    </div>
  );
}

function Phase({ phase, onTaskClick, onHeaderClick }) {
  return (
    <div className="flex items-stretch gap-3" data-testid={`reconstruction-phase-${phase.num}`}>
      <Marker text={phase.leftMarker} side="left" />
      <div
        className="flex-1 rounded-[32px] px-4 sm:px-6 py-4 relative overflow-hidden"
        style={{ background: phase.gradient, minHeight: '128px' }}
      >
        <button
          type="button"
          onClick={() => onHeaderClick(phase)}
          data-testid={`reconstruction-phase-header-${phase.num}`}
          className="flex items-center gap-3 mb-3 transition-opacity hover:opacity-90"
        >
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
            style={{ backgroundColor: 'white', color: phase.accent, fontFamily: 'Manrope' }}
          >
            {phase.num}
          </span>
          <h3 className="text-sm sm:text-base font-bold text-white tracking-wide text-left" style={{ fontFamily: 'Manrope' }}>
            {phase.title}
          </h3>
        </button>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {phase.tasks.map((task, i) => (
            <button
              type="button"
              key={task.name}
              onClick={() => onTaskClick(phase, task)}
              className="rounded-xl px-2.5 py-2 text-center text-[10px] sm:text-xs font-bold leading-tight flex items-center justify-center min-h-[56px] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'white',
                color: '#1A2E2E',
                fontFamily: 'Manrope',
                border: `1.5px solid ${phase.accent}80`,
              }}
              data-testid={`reconstruction-task-${phase.num}-${i}`}
            >
              {task.name}
            </button>
          ))}
        </div>
      </div>
      <Marker text={phase.rightMarker} side="right" />
    </div>
  );
}

export default function ReconstructionRoadmap() {
  const [selected, setSelected] = useState(null);
  const [livePhases, setLivePhases] = useState([]);

  // Fetch live phases from the public roadmaps endpoint. Any phase
  // tagged with department === <band.title> will replace the fallback
  // milestones for that band. Untagged phases are ignored here (they
  // still show on the per-department vertical timeline strip).
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/planning/roadmaps`)
      .then(({ data }) => {
        if (cancelled) return;
        const flat = [];
        (data || []).forEach((rm) => (rm.phases || []).forEach((ph) => flat.push(ph)));
        setLivePhases(flat);
      })
      .catch(() => { if (!cancelled) setLivePhases([]); });
    return () => { cancelled = true; };
  }, []);

  // Build the displayed phases: for each hard-coded band, look for
  // live phases tagged with that department. If any are found, swap
  // the band's `tasks` for those phases (date-suffixed). Otherwise
  // keep the fallback so the section never looks empty.
  const displayPhases = useMemo(() => {
    return PHASES.map((band) => {
      const liveForBand = livePhases.filter((p) => (p.department || '').trim() === band.title);
      if (liveForBand.length === 0) return band;
      // Sort live phases by start_date so the tile order is chronological.
      liveForBand.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
      const tasks = liveForBand.map((p) => {
        const dateSuffix = fmtShort(p.start_date);
        const hasDate = /·|\b20\d{2}\b/.test(p.name || '');
        return {
          name: hasDate || !dateSuffix ? (p.name || '(untitled)') : `${p.name} · ${dateSuffix}`,
          description: p.description || `${p.name} — ${p.start_date || ''} to ${p.end_date || ''}`,
          _live: true,
        };
      });
      return { ...band, tasks, _hasLive: true };
    });
  }, [livePhases]);

  const onTaskClick = (phase, task) => {
    setSelected({
      name: task.name,
      description: task.description,
      kicker: `${phase.title} Department${phase._hasLive ? ' · Live' : ''}`,
      parent: { name: phase.title, accent: phase.accent },
      accent: phase.accent,
      // Tasks themselves don't have separate dates — they inherit the
      // parent phase timeline so the user gets the rough window for the
      // task without us inventing per-task dates.
      start_date: null,
      end_date: null,
    });
  };

  const onHeaderClick = (phase) => {
    setSelected({
      name: phase.title,
      description: phase.summary,
      kicker: `${phase.title} Department · ${phase.timeline}${phase._hasLive ? ' · Live' : ''}`,
      accent: phase.accent,
      children: phase.tasks.map((t) => ({ id: t.name, title: t.name, description: t.description })),
    });
  };

  return (
    <section
      data-testid="reconstruction-roadmap-section"
      className="py-16 px-4 sm:px-6 lg:px-10"
      style={{ backgroundColor: '#FBF4E4' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: '#92400E', color: '#FEF3C7' }}>
            <MilestoneIcon className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'Manrope' }}>
              Hotel Recovery View · Internal
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#451A03', fontWeight: 500 }}
          >
            Per-Team Reopening Plans
          </h2>
          <p className="text-base italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#92400E' }}>
            Hotel-side departmental view · runs alongside the contractor Pacer roadmap above.
          </p>
          <p className="mt-2 text-[11px] italic" style={{ color: '#92400E', fontFamily: 'Manrope' }}>
            Tap any department title or milestone tile to read the full description.
          </p>
        </div>

        {/* Phases */}
        <div className="space-y-3">
          {displayPhases.map((phase) => (
            <Phase
              key={phase.num}
              phase={phase}
              onTaskClick={onTaskClick}
              onHeaderClick={onHeaderClick}
            />
          ))}
        </div>

        {/* GM KPIs footer */}
        <div className="mt-10 flex justify-end">
          <div
            className="inline-flex items-start gap-4 px-5 py-3 rounded-2xl max-w-md"
            style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#1A2E2E', color: '#A3D4D4' }}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18" />
                <path d="m7 14 4-4 4 4 5-5" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
                GM KPIs
              </p>
              <ul className="space-y-0.5">
                {KPIS.map((k) => (
                  <li key={k} className="flex items-start gap-1.5 text-[11px]" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
                    <span className="mt-[5px] w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: '#1A2E2E' }} />
                    {k}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <MilestoneDetailDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        milestone={selected}
      />
    </section>
  );
}
